/**
 * Meta Pixel tracking utility
 * - Production-only: Events only fire in production
 * - SSR-safe: All functions check for window/document
 */

import { isValidTrackingEmail } from "@/utils/tiktok-pixel"

// Type for fbq function (window.fbq is declared as any in RouterEventWrapper.tsx)
type FbqFunction = (
  action: 'track' | 'trackCustom' | 'init',
  eventName: string,
  params?: MetaEventParams,
  options?: { eventID?: string }
) => void

// Standard Meta Pixel event parameters
export interface MetaEventParams {
  content_name?: string
  content_category?: string
  content_ids?: string[]
  content_type?: string
  value?: number
  currency?: string
  num_items?: number
  predicted_ltv?: number
  search_string?: string
  status?: string
  // Custom params
  [key: string]: string | number | string[] | undefined
}

// Attribution data from cookies
export interface MetaAttributionData {
  fbp: string | null
  fbc: string | null
}

/**
 * Track a Meta Pixel event
 * @param eventName - Standard event name (e.g., 'InitiateCheckout', 'Purchase', 'ViewContent')
 * @param params - Event parameters
 * @param eventId - Optional event ID for deduplication with server-side events
 */
export function trackMetaEvent(
  eventName: string,
  params?: MetaEventParams,
  eventId?: string
): void {
  // Production-only gate
  if (process.env.NODE_ENV !== 'production') return

  // SSR safety
  if (typeof window === 'undefined') return

  // Check if fbq is loaded
  const fbq = (window as unknown as { fbq?: FbqFunction }).fbq
  if (!fbq) {
    console.warn('[Meta Pixel] fbq not loaded')
    return
  }

  try {
    if (eventId) {
      fbq('track', eventName, params, { eventID: eventId })
    } else {
      fbq('track', eventName, params)
    }
  } catch (error) {
    console.error('[Meta Pixel] Error tracking event:', error)
  }
}

/**
 * Generate a deterministic event ID for deduplication
 * Format: {prefix}_{identifier}_{YYYYMMDD}
 * @param prefix - Event type prefix (e.g., 'checkout', 'purchase')
 * @param identifier - Unique identifier (e.g., user ID, session ID)
 */
export function generateEventId(prefix: string, identifier: string): string {
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = String(now.getUTCMonth() + 1).padStart(2, '0')
  const day = String(now.getUTCDate()).padStart(2, '0')
  const dateStr = `${year}${month}${day}`

  return `${prefix}_${identifier}_${dateStr}`
}

/**
 * Get Meta attribution data from cookies
 * Returns fbp (browser ID) and fbc (click ID) for server-side event matching
 * Supports both 'fbp'/'fbc' and '_fbp'/'_fbc' cookie formats
 */
export function getMetaAttributionData(): MetaAttributionData {
  // SSR safety
  if (typeof document === 'undefined') {
    return { fbp: null, fbc: null }
  }

  const cookies = document.cookie.split(';').reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split('=')
      if (key) acc[key] = value || ''
      return acc
    },
    {} as Record<string, string>
  )

  // Check both formats: 'fbp'/'_fbp' and 'fbc'/'_fbc'
  const fbp = cookies['_fbp'] || cookies['fbp'] || null
  const fbc = cookies['_fbc'] || cookies['fbc'] || null

  return { fbp, fbc }
}

/**
 * Set Meta Advanced Matching data by re-initializing the pixel with user info.
 * Calling fbq('init', pixelId, userData) again updates Advanced Matching params.
 * Meta's pixel JS handles email hashing automatically — do NOT hash client-side.
 * @param pixelId - The Meta Pixel ID (must match the one used in initial init)
 * @param email - User's plain-text email address
 * @param userId - Optional user ID for external_id matching (improves EMQ)
 */
export function setMetaAdvancedMatching(pixelId: string, email: string, userId?: string): void {
  // Production-only gate
  if (process.env.NODE_ENV !== 'production') return

  // SSR safety
  if (typeof window === 'undefined') return

  const fbq = (window as unknown as { fbq?: FbqFunction }).fbq
  if (!fbq) {
    console.warn('[Meta Pixel] fbq not loaded, cannot set Advanced Matching')
    return
  }

  const normalizedEmail = email.toLowerCase().trim()
  if (!isValidTrackingEmail(normalizedEmail)) {
    console.warn('[Meta Pixel] Invalid email, skipping Advanced Matching:', email ? '[non-empty invalid]' : '[empty]')
    return
  }

  try {
    const userData: Record<string, string> = { em: normalizedEmail }
    if (userId) {
      userData.external_id = userId
    }
    fbq('init', pixelId, userData as any)

    // Persist for returning visitor identify on marketing pages (shared with TikTok)
    try {
      const d = new Date()
      d.setTime(d.getTime() + 7776e6) // 90 days
      // Only write if not already set (tiktok-pixel.ts may have set them)
      if (!document.cookie.includes('_of_em=')) {
        document.cookie = `_of_em=${encodeURIComponent(normalizedEmail)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
      }
      if (userId && !document.cookie.includes('_of_uid=')) {
        document.cookie = `_of_uid=${encodeURIComponent(userId)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
      }
    } catch (e) {
      // Cookie write failure is non-critical
    }
  } catch (error) {
    console.error('[Meta Pixel] Error setting Advanced Matching:', error)
  }
}
